import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  Divider,
  Paper,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { storage, auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { collection, doc, setDoc, serverTimestamp, getDoc, deleteDoc } from 'firebase/firestore';
import * as amplitude from '@amplitude/analytics-browser';

/**
 * Reads OGG duration by parsing the file's binary headers directly.
 * Reads only the first 4 KB (for codec/sample-rate) and the last 64 KB
 * (for the final granule position), so it stays fast for files of any size.
 * Returns duration in seconds, or null if the file can't be parsed.
 */
/**
 * Detects the real audio MIME type by inspecting the file's magic bytes,
 * regardless of the file extension or the browser-assigned type.
 * This handles common cases like M4A files saved with a .ogg extension.
 */
async function detectAudioMimeType(file) {
  try {
    const buf = await file.slice(0, 12).arrayBuffer();
    const b = new Uint8Array(buf);

    // MP4 / M4A container: box-size (4 bytes) then "ftyp" at bytes 4-7
    if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) {
      return 'audio/x-m4a';
    }
    // OGG container
    if (b[0] === 0x4f && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) {
      return 'audio/ogg';
    }
    // MP3: ID3 tag header
    if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) {
      return 'audio/mpeg';
    }
    // MP3: raw MPEG sync word (0xFF 0xEx or 0xFF 0xFx)
    if (b[0] === 0xff && (b[1] & 0xe0) === 0xe0) {
      return 'audio/mpeg';
    }
    // WAV: RIFF....WAVE
    if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {
      return 'audio/wav';
    }
    // FLAC
    if (b[0] === 0x66 && b[1] === 0x4c && b[2] === 0x61 && b[3] === 0x43) {
      return 'audio/flac';
    }
  } catch (_) {
    // Ignore read errors; caller falls back to file.type
  }
  return file.type;
}

async function getOggDuration(file) {
  try {
    // --- Step 1: detect codec and sample rate from the first OGG pages ---
    const headBuf = await file.slice(0, 4096).arrayBuffer();
    const head = new Uint8Array(headBuf);
    const headDV = new DataView(headBuf);

    let sampleRate = 48000; // Opus always uses 48 kHz for granule timing
    for (let i = 0; i < head.length - 16; i++) {
      // "Opus" (4f 70 75 73) → OpusHead packet
      if (head[i] === 79 && head[i + 1] === 112 && head[i + 2] === 117 && head[i + 3] === 115) {
        sampleRate = 48000;
        break;
      }
      // \x01vorbis → Vorbis ID header; sample rate is at offset +12 (4 bytes LE)
      if (head[i] === 1 && head[i + 1] === 118 && head[i + 2] === 111 && head[i + 3] === 114) {
        const rate = headDV.getUint32(i + 12, true);
        if (rate > 0) sampleRate = rate;
        break;
      }
    }

    // --- Step 2: find the last OGG page in the tail of the file ---
    const tailStart = Math.max(0, file.size - 65536);
    const tailBuf = await file.slice(tailStart).arrayBuffer();
    const tail = new Uint8Array(tailBuf);
    const tailDV = new DataView(tailBuf);

    for (let i = tail.length - 4; i >= 0; i--) {
      // "OggS" capture pattern (4f 67 67 53)
      if (tail[i] === 79 && tail[i + 1] === 103 && tail[i + 2] === 103 && tail[i + 3] === 83) {
        if (i + 14 > tail.length) continue;
        const lo = tailDV.getUint32(i + 6, true);
        const hi = tailDV.getUint32(i + 10, true);
        if (lo === 0xffffffff && hi === 0xffffffff) continue; // unset granule
        // Combine low and high 32-bit words into a single number.
        // Number can represent integers exactly up to 2^53, which covers
        // ~6 million hours at 48 kHz — more than enough.
        const granule = lo + hi * 4294967296;
        if (granule > 0) return Math.ceil(granule / sampleRate);
      }
    }
  } catch (_) {
    // Parsing failed; caller will fall back to the HTML audio element.
  }
  return null;
}

const NewAudio = () => {
  // Common State
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const [audioDuration, setAudioDuration] = useState(null);
  const [currentDocId, setCurrentDocId] = useState(null);
  const [currentUploadTask, setCurrentUploadTask] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  // Upload-specific State
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  // Record-specific State
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const audioChunks = useRef([]);
  const [recordingComplete, setRecordingComplete] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const startTimeRef = useRef(null);
  const [displayTimer, setDisplayTimer] = useState(0);
  const displayTimerRef = useRef(null);

  // Combined active state
  const isBusy = recording || uploading || selectedFile || recordingComplete;

  useEffect(() => {
    amplitude.track('Record/Upload Page Viewed');
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        setUserEmail(user.email || '');
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setRemainingSeconds(userDoc.data().remaining_seconds || 0);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          amplitude.track('Error Fetching User Data', { error: error.message });
        }
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    // Cleanup stream on component unmount to ensure the mic indicator turns off
    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [audioStream]);

  const formatTime = (seconds) => {
    if (seconds === null || isNaN(seconds)) return '0 րոպե 0 վրկ';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours ? hours + ' ժ ' : ''}${minutes} րոպե ${secs} վրկ`;
  };

  const formatDisplayTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  const resetUploadAndRecordingState = () => {
    if (displayTimerRef.current) {
      clearInterval(displayTimerRef.current);
    }
    setSelectedFile(null);
    setAudioDuration(null);
    setInsufficientCredits(false);
    setUploading(false);
    setUploadProgress(0);
    setCurrentUploadTask(null);
    setCurrentDocId(null);
    setRecording(false);
    setMediaRecorder(null);
    setAudioStream(null);
    setRecordingComplete(false);
    setAudioBlob(null);
    setDisplayTimer(0);
    audioChunks.current = [];
  };

  const handleCancelAll = async (shouldCancelUpload = true) => {
    if (shouldCancelUpload && uploading && currentUploadTask) {
      try {
        currentUploadTask.cancel();
        amplitude.track('Upload Cancelled By User');
        if (currentDocId) {
          await deleteDoc(doc(db, 'recordings', currentDocId));
        }
      } catch (error) {
        console.error('Error cancelling upload task:', error);
      }
    }
    if (recording && mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
    if (audioStream) {
      audioStream.getTracks().forEach((track) => track.stop());
    }
    resetUploadAndRecordingState();
  };

  // --- Upload Logic ---
  const handleFileChange = (file) => {
    if (!file) {
      alert('Խնդրում ենք ընտրել վավեր աուդիո ֆայլ։');
      amplitude.track('Invalid File Selected', { error: 'No file selected' });
      return;
    }
    if (file.type.startsWith('video/')) {
      alert('Տեսանյութերը չեն աջակցվում այս պահին։ Խնդրում ենք վերբեռնել միայն աուդիո ֆայլեր։');
      amplitude.track('Video File Rejected', { fileName: file.name });
      return;
    }
    // Detect the real format from magic bytes — handles files with wrong extensions
    // (e.g. iPhone Voice Memos M4A saved as .ogg).
    detectAudioMimeType(file).then((effectiveType) => {
      const proceedWithDuration = (durationSeconds) => {
        if (remainingSeconds < durationSeconds) {
          setAudioDuration(durationSeconds);
          setSelectedFile(file);
          setInsufficientCredits(true);
          amplitude.track('Insufficient Credits for File', { fileName: file.name, duration: durationSeconds });
        } else {
          amplitude.track('File Upload Started Immediately', { fileName: file.name, duration: durationSeconds });
          uploadGenericAudio(file, durationSeconds, 'upload', effectiveType, (file.size / (1024 * 1024)));
        }
      };

      const loadDurationFromAudioElement = () => {
        const audio = document.createElement('audio');
        audio.preload = 'metadata';
        const objectUrl = URL.createObjectURL(file);
        audio.src = objectUrl;

        // Safety timeout: onloadedmetadata can silently stall on large OGG files.
        const timer = setTimeout(() => {
          audio.src = '';
          URL.revokeObjectURL(objectUrl);
          amplitude.track('File Metadata Timeout', { fileName: file.name, effectiveType });
          // Estimate: assume ~96 kbps average (12 000 bytes/s) as a middle ground.
          proceedWithDuration(Math.max(1, Math.ceil(file.size / 12000)));
        }, 8000);

        audio.onloadedmetadata = () => {
          clearTimeout(timer);
          URL.revokeObjectURL(objectUrl);
          const durationSeconds = isFinite(audio.duration)
            ? Math.round(audio.duration)
            : Math.max(1, Math.ceil(file.size / 12000)); // Infinity fallback
          proceedWithDuration(durationSeconds);
        };
        audio.onerror = () => {
          clearTimeout(timer);
          URL.revokeObjectURL(objectUrl);
          alert('Չհաջողվեց կարդալ ֆայլը։');
          amplitude.track('File Read Error', { fileName: file.name });
        };
      };

      const isOgg = effectiveType === 'audio/ogg';
      if (isOgg) {
        // Parse OGG headers directly — fast and works regardless of file size.
        // Falls back to the HTML audio element if the binary parse fails.
        getOggDuration(file).then((duration) => {
          if (duration && duration > 0) {
            amplitude.track('OGG Duration from Headers', { fileName: file.name, duration });
            proceedWithDuration(duration);
          } else {
            loadDurationFromAudioElement();
          }
        });
      } else {
        loadDurationFromAudioElement();
      }
    });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) handleFileChange(file);
    event.target.value = null;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isBusy) setDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (!isBusy) {
      const file = e.dataTransfer.files[0];
      if (file) handleFileChange(file);
    }
  };

  // --- Record Logic ---
  const startRecording = async () => {
    try {
      amplitude.track('Recording Started');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: true,
          autoGainControl: true,
        }
      });
      setAudioStream(stream);
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      setMediaRecorder(recorder);
      startTimeRef.current = new Date();
      setDisplayTimer(0);
      displayTimerRef.current = setInterval(() => setDisplayTimer((prev) => prev + 1), 1000);
      recorder.ondataavailable = (event) => audioChunks.current.push(event.data);
      recorder.onstop = () => {
        const blob = new Blob(audioChunks.current, { type: 'audio/webm' });
        audioChunks.current = [];
        clearInterval(displayTimerRef.current);
        const duration = Math.round((new Date() - startTimeRef.current) / 1000);

        if (stream) stream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
        setMediaRecorder(null);
        setRecording(false);

        if (duration > remainingSeconds) {
          setAudioDuration(duration);
          setAudioBlob(blob);
          setRecordingComplete(true);
          setInsufficientCredits(true);
          amplitude.track('Recording Insufficient Credits', { duration });
        } else {
          amplitude.track('Recording Upload Started Immediately', { duration });
          uploadGenericAudio(blob, duration, 'recording', 'audio/webm', (blob.size / (1024 * 1024)));
        }
      };
      recorder.start();
      setRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      amplitude.track('Recording Microphone Error', { error: error.message });
      alert('Հնարավոր չէ միացնել խոսափողը։');
      handleCancelAll();
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }
  };

  // --- Generic Upload Logic ---
  const uploadGenericAudio = async (fileOrBlob, duration, source, fileType, size) => {
    setUploading(true);
    setInsufficientCredits(false);
    setRecordingComplete(false);
    setSelectedFile(source === 'upload' ? fileOrBlob : null);
    setAudioBlob(source === 'recording' ? fileOrBlob : null);
    setAudioDuration(duration);

    try {
      amplitude.track('Audio Upload Started', { duration, source });
      const user = auth.currentUser;
      if (!user) {
        alert('Դուք պետք է մուտք գործեք համակարգ։');
        handleCancelAll();
        return;
      }
      const sizeMB = size.toFixed(2);
      const recordingsRef = collection(db, 'recordings');
      const docRef = doc(recordingsRef);
      const docId = docRef.id;
      setCurrentDocId(docId);
      await setDoc(docRef, {
        userId: user.uid, status: 'uploading', createdAt: serverTimestamp(), duration, fileType, source, size: parseFloat(sizeMB),
        ...(source === 'upload' && fileOrBlob.name ? { title: fileOrBlob.name } : {}),
      });
      const fileName = source === 'upload' ? `${docId}-${fileOrBlob.name}` : `${docId}.webm`;
      const storageRef = ref(storage, `recordings/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, fileOrBlob, { contentType: fileType, customMetadata: { uid: user.uid, docId } });
      setCurrentUploadTask(uploadTask);
      uploadTask.on('state_changed',
        (snapshot) => setUploadProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        (error) => {
          if (error.code !== 'storage/canceled') {
            console.error('Upload failed:', error);
            alert('Ֆայլի վերբեռնումը ձախողվեց։');
            amplitude.track('Audio Upload Failed', { error: error.message, source });
          }
          handleCancelAll();
        },
        () => {
          amplitude.track('Audio Upload Successful', { docId, duration, source });
          navigate(`/recording-details/${encodeURIComponent(docId)}`);
          handleCancelAll(false);
        }
      );
    } catch (error) {
      console.error('Error uploading audio:', error);
      amplitude.track('Audio Upload Error', { error: error.message, source });
      handleCancelAll();
    }
  };

  const handleBuyCredits = () => {
    amplitude.track('Buy Credits Clicked');
    navigate('/pricing');
  };

  const renderInitialView = () => (
    <Grid container spacing={4} alignItems="stretch">
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', p: { xs: 2, sm: 3 } }}>
            <MicIcon sx={{ fontSize: 60, color: '#229ED9', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Ձայնագրել
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ձայնագրեք ձեր խոսքը, և մենք այն կվերածենք տեքստի։
            </Typography>
            <Button
              variant="contained"
              onClick={startRecording}
              disabled={isBusy}
              size="large"
            >
              Սկսել ձայնագրումը
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 3 }, boxSizing: 'border-box' }}>
            <Paper
              elevation={0}
              sx={{ flex: 1, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: { xs: 2, sm: 3 }, bgcolor: 'rgba(34, 158, 217, 0.08)', borderRadius: 2, border: '2px dashed', borderColor: 'rgba(34, 158, 217, 0.2)', minHeight: 0 }}
            >
              <CloudUploadIcon sx={{ fontSize: 60, color: '#229ED9', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Վերբեռնել ֆայլ
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Քաշեք և թողեք</b> ձեր աուդիո ֆայլը այստեղ
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                կամ
              </Typography>
              <Button
                variant="contained"
                component="label"
                disabled={isBusy}
              >
                Ընտրեք ֆայլը
                <input type="file" hidden onChange={handleFileSelect} accept="audio/mpeg,audio/mp3,.mp3,audio/x-m4a,audio/m4a,audio/mp4,.m4a,audio/wav,.wav,audio/webm,.webm,audio/ogg,.ogg,audio/flac,.flac,audio/aac,.aac" disabled={isBusy} />
              </Button>
            </Paper>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderActiveView = () => (
    <Card sx={{ maxWidth: 600, width: '100%', mx: 'auto' }}>
      <CardContent>
        {recording && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>{formatDisplayTime(displayTimer)}</Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>Տեքստը կվերծանվի ձայնագրությունը ավարտելուց հետո։</Typography>
            <Button variant="contained" color="error" onClick={stopRecording} sx={{ mt: 3, px: 4, py: 1.5 }} size="large">Ավարտել</Button>
          </Box>
        )}
        {(uploading || selectedFile || recordingComplete) && (
          <Box>
            <Typography variant="h6" gutterBottom textAlign="center">
              {uploading ? 'Ֆայլի վերբեռնում' : 'Ֆայլի տվյալներ'}
            </Typography>
            <Grid container spacing={2} sx={{ my: 2 }}>
              <Grid item xs={6}><Typography variant="body2">Տևողություն:</Typography><Typography variant="subtitle2">{formatTime(audioDuration)}</Typography></Grid>
              <Grid item xs={6}><Typography variant="body2">Հասանելի ժամանակ:</Typography><Typography variant="subtitle2" sx={{ color: insufficientCredits ? 'error.main' : 'text.primary' }}>{formatTime(remainingSeconds)}</Typography></Grid>
              {selectedFile && <><Grid item xs={6}><Typography variant="body2">Ֆայլի անուն:</Typography><Typography noWrap variant="subtitle2">{selectedFile.name}</Typography></Grid><Grid item xs={6}><Typography variant="body2">Չափս:</Typography><Typography variant="subtitle2">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</Typography></Grid></>}
              {audioBlob && <><Grid item xs={6}><Typography variant="body2">Ֆայլի տիպ:</Typography><Typography variant="subtitle2">Ձայնագրություն</Typography></Grid><Grid item xs={6}><Typography variant="body2">Չափս:</Typography><Typography variant="subtitle2">{(audioBlob.size / (1024 * 1024)).toFixed(2)} MB</Typography></Grid></>}
            </Grid>
            <Divider sx={{ my: 2 }} />
            {uploading ? (
              <Box>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>Վերբեռնում {Math.round(uploadProgress)}%</Typography>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="outlined" color="error" onClick={() => handleCancelAll(true)}>
                    Չեղարկել վերբեռնումը
                  </Button>
                </Box>
              </Box>
            ) : insufficientCredits ? (
              <Box textAlign="center">
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>Վերծանման համար անհրաժեշտ է գնել լրացուցիչ ժամանակ։</Typography>

                <Box display="flex" justifyContent="space-around" sx={{ mt: 2 }}><Button variant="contained" sx={{ bgcolor: '#ff5722', '&:hover': { bgcolor: '#e64a19' } }} onClick={handleBuyCredits} startIcon={<CreditCardIcon />}>Գնել ժամանակ</Button><Button variant="outlined" onClick={handleCancelAll}>Չեղարկել</Button></Box>
              </Box>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 100px)', width: '100%', p: { xs: 2, sm: 3 }, boxSizing: 'border-box', backgroundColor: '#f5f5f5' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragging && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, pointerEvents: 'none', backgroundColor: 'rgba(200, 225, 255, 0.95)' }}>
          <Typography variant="h4" color="primary">Բաց թողեք վերբեռնելու համար</Typography>
        </Box>
      )}
      <Box sx={{ width: '100%', maxWidth: '1100px' }}>
        {!isBusy ? renderInitialView() : renderActiveView()}
      </Box>
    </Box>
  );
};

export default NewAudio;
